import React from "react";
import Select from "react-select";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { ScheduleResourceResponse } from "app/utils/request/scheduling/facilityResources";
import { SelectStyles } from "./constants";

type PropsDef = {
  name: string;
  // "auto" for a lesson with an option, "none" for a custom lesson.
  emptyChoice: "auto" | "none";
  // The bays the select offers, in the order of the Resources page.
  bays: ScheduleResourceResponse[];
  // Gets the chosen bay id, or null for "Auto" and "No resource".
  onSelect: (scheduleResourceId: number | null) => void;
  // The bay the lesson holds when it is not in the list (an inactive bay).
  // The select shows it as the value, and the menu does not offer it.
  heldBay?: { id: number; title: string } | null;
  disabled?: boolean;
};

export type ResourceOption = { id: number | null; title: string };
export type ResourceGroup = { label: string; options: ResourceOption[] };

// The bays in groups by section. The list is in section order, so the bays of
// one section are next to each other. The block form uses the same groups.
export function groupBySection(bays: ScheduleResourceResponse[]): ResourceGroup[] {
  return bays.reduce<ResourceGroup[]>((groups, bay) => {
    const option = { id: bay.id, title: bay.title };
    const last = groups[groups.length - 1];
    if (last && last.label === bay.resourceCategory.title) {
      last.options.push(option);
    } else {
      groups.push({ label: bay.resourceCategory.title, options: [option] });
    }
    return groups;
  }, []);
}

/**
 * The bay of a lesson in the event form. The first entry is "Auto" for a
 * lesson with an option and "No resource" for a custom lesson. The bays come
 * after it, in groups by section.
 */
export default function ResourceSelect({
  name,
  emptyChoice,
  bays,
  onSelect,
  heldBay = null,
  disabled = false,
}: PropsDef) {
  const { control } = useFormContext();
  const { t } = useTranslation();

  const emptyOption: ResourceOption = {
    id: null,
    title: emptyChoice === "auto" ? t("Auto") : t("No resource"),
  };
  const groups = groupBySection(bays);
  const allOptions = [emptyOption, ...groups.flatMap((group) => group.options)];
  const heldOption: ResourceOption | null = heldBay
    ? { id: heldBay.id, title: `${heldBay.title} (${t("inactive")})` }
    : null;

  const valueFor = (value: number | null | undefined) => {
    const id = value ?? null;
    const listed = allOptions.find((opt) => opt.id === id);
    if (listed) {
      return listed;
    }
    return heldOption && heldOption.id === id ? heldOption : null;
  };

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <Select
          styles={SelectStyles}
          aria-label={t("Resource")}
          getOptionLabel={(opt: ResourceOption) => opt.title}
          getOptionValue={(opt: ResourceOption) => String(opt.id ?? "")}
          options={[emptyOption, ...groups]}
          isSearchable={false}
          placeholder={t("Select")}
          value={valueFor(field.value)}
          onChange={(opt: ResourceOption) => onSelect(opt?.id ?? null)}
          onBlur={field.onBlur}
          isDisabled={disabled}
        />
      )}
    />
  );
}
